import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import './styles/admin.scss';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import ConfirmModal from '../../components/ConfirmModal';
import { SUBJECTS } from './adminConstants';

type Range = { _k?: string; subject: number; count: number; timeMinutes: number };
type Faculty = { id?: string; name: string; examDate?: string | null; testModel: number; ranges: Range[] };

const MODELS = [{ v: 0, l: "Jednoduchý výběr" }, { v: 1, l: "Jednoduchý + žádná správná" }, { v: 2, l: "Kombinovaný" }];

let rangeSeq = 0;
const nextRangeKey = () => "rg" + (++rangeSeq);

export default function AdminFacultyEditor() {
    const { id } = useParams();
    const navigate = useNavigate();
    const api = new ApiHelper();
    const isNew = id === "new";

    const [f, setF] = useState<Faculty | null>(null);
    const [confirmDel, setConfirmDel] = useState(false);

    useEffect(() => { load(); }, [id]);
    async function load() {
        if (isNew) { setF({ name: "", examDate: "", testModel: 0, ranges: [] }); return; }
        try {
            const data: Faculty = await (await api.adminFacultiesApi.detail(id!)).json();
            data.ranges = (data.ranges ?? []).map(r => ({ ...r, _k: nextRangeKey() }));
            setF(data);
        } catch (x: any) { api.adminError(x); }
    }

    function setRange(i: number, patch: Partial<Range>) { if (f) setF({ ...f, ranges: f.ranges.map((r, idx) => idx === i ? { ...r, ...patch } : r) }); }
    function addRange() {
        if (!f) return;
        const used = new Set(f.ranges.map(r => r.subject));
        const next = SUBJECTS.find(s => !used.has(s.v));
        if (!next) { api.alert.error("Všechny předměty už jsou přidané"); return; }
        setF({ ...f, ranges: [...f.ranges, { subject: next.v, count: 10, timeMinutes: 30, _k: nextRangeKey() }] });
    }
    function removeRange(i: number) { if (f) setF({ ...f, ranges: f.ranges.filter((_, idx) => idx !== i) }); }

    async function save() {
        if (!f) return;
        if (!f.name.trim()) { api.alert.error("Zadej název fakulty"); return; }
        const subjects = f.ranges.map(r => r.subject);
        if (new Set(subjects).size !== subjects.length) { api.alert.error("Každý předmět může být v testu jen jednou"); return; }
        const payload = { name: f.name, examDate: f.examDate || null, testModel: f.testModel, ranges: f.ranges };
        try {
            if (isNew) { const r = await (await api.adminFacultiesApi.create(payload)).json(); api.alert.success("Fakulta vytvořena"); navigate("/admin/fakulty/" + r.id, { replace: true }); }
            else { await api.adminFacultiesApi.update(id!, payload); api.alert.success("Uloženo"); await load(); }
        } catch (x: any) { api.adminError(x); }
    }
    async function del() { try { await api.adminFacultiesApi.remove(id!); api.alert.success("Smazáno"); navigate("/admin/fakulty"); } catch (x: any) { api.adminError(x); } }

    if (!f) return <div><span className="backLink" onClick={() => navigate("/admin/fakulty")}>← Fakulty</span></div>;

    return (
        <div>
            <span className="backLink" onClick={() => navigate("/admin/fakulty")}>← Fakulty</span>
            <div className="adminHead">
                <div><h1>{isNew ? "Nová fakulta" : f.name}</h1></div>
                <div className="aActions">
                    {!isNew && <button className="aBtn danger" onClick={() => setConfirmDel(true)}>Smazat</button>}
                    <button className="aBtn" onClick={save}>{isNew ? "Vytvořit" : "Uložit"}</button>
                </div>
            </div>

            <div className="aGrid">
                <div className="aCard">
                    <h2>Základ</h2>
                    <div className="aField"><label>Název</label><input value={f.name} onChange={e => setF({ ...f, name: e.target.value })} /></div>
                    <div className="aField"><label>Termín přijímaček (volitelné)</label><input type="date" value={f.examDate ? f.examDate.slice(0, 10) : ""} onChange={e => setF({ ...f, examDate: e.target.value || null })} /></div>
                    <div className="aField"><label>Model testu</label><select value={f.testModel} onChange={e => setF({ ...f, testModel: Number(e.target.value) })}>{MODELS.map(m => <option key={m.v} value={m.v}>{m.l}</option>)}</select></div>
                </div>

                <div className="aCard">
                    <h2>Rozsah modelového testu</h2>
                    <div className="rowList">
                        {f.ranges.map((r, i) => (
                            <div key={r._k ?? i} className="matRow" style={{ gridTemplateColumns: "1fr 110px 110px auto" }}>
                                <select value={r.subject} onChange={e => setRange(i, { subject: Number(e.target.value) })}>{SUBJECTS.map(s => <option key={s.v} value={s.v} disabled={s.v !== r.subject && f.ranges.some(x => x.subject === s.v)}>{s.l}</option>)}</select>
                                <input type="number" min={1} value={r.count} onChange={e => setRange(i, { count: Number(e.target.value) })} title="Počet otázek" />
                                <input type="number" min={1} value={r.timeMinutes} onChange={e => setRange(i, { timeMinutes: Number(e.target.value) })} title="Minut" />
                                <span className="x" onClick={() => removeRange(i)}>×</span>
                            </div>
                        ))}
                        {f.ranges.length === 0 && <span className="empty">Bez rozsahu — modelový test nebude dostupný.</span>}
                    </div>
                    <button className="aBtn ghost" style={{ marginTop: "10px" }} onClick={addRange}>+ Přidat předmět</button>
                    <p className="hint" style={{ marginTop: "10px" }}>Počet otázek a časový limit (minuty) pro každý předmět modelového testu.</p>
                </div>
            </div>

            <ConfirmModal open={confirmDel} title="Smazat fakultu?" message="Fakulta bude smazána; studentům s tímto cílem se cíl zruší." confirmLabel="Smazat" cancelLabel="Zrušit" onConfirm={() => { setConfirmDel(false); del(); }} onClose={() => setConfirmDel(false)} />
        </div>
    );
}
